/**
 * Die Fokusfalle für Dialoge — Tab bleibt drin, und beim Schließen geht der
 * Fokus dahin zurück, wo er herkam.
 *
 * ## Der Befund
 *
 * Kein Dialog im Bestand hatte eine. Reibung, Kopierwahl, Befehlssuche und
 * Titelbildwahl haben alle vier einen Anfangsfokus, Escape und einen Vorhang —
 * wer aber weiter Tab drückt, wandert **hinter** den Dialog in die Seite
 * darunter: in Reiter, die verdeckt sind, in Knöpfe, die man nicht sieht, und
 * in Felder eines Formulars, das gerade nicht zur Debatte steht. Mit
 * Tastatur oder Sprachausgabe ist der Dialog damit kein Dialog, sondern eine
 * Fläche, die zufällig oben liegt.
 *
 * Und zurück geht der Fokus danach auch nicht. Wer die Befehlssuche mit
 * Escape schließt, steht anschließend am Anfang der Seite, nicht an der Lupe,
 * die er gedrückt hat.
 *
 * ## Warum ein gemeinsamer Griff und keine vier Nachrüstungen
 *
 * Weil es ein Muster ist und kein Einzelfall. Eine Fokusfalle an einem von
 * vier Dialogen ist die schlechteste aller Fassungen: Die Tastatur verhält
 * sich dann je nach Dialog anders, und niemand kann sagen, welche Regel gilt.
 * Als Svelte-Action steht sie an einer Stelle, hängt mit `use:fokusfalle` an
 * jedem Rahmen mit `role="dialog"`, und `tests/muster.test.mjs` hält fest,
 * dass alle vier sie tragen.
 *
 * ## Was sie nicht tut
 *
 * Sie setzt **keinen** Anfangsfokus. Den haben alle vier Dialoge schon, und
 * jeder setzt ihn dorthin, wo er hingehört — die Befehlssuche ins Suchfeld,
 * die drei Bestätigungsdialoge auf „Abbrechen", also auf den harmlosen Ausweg.
 * Eine Action, die das überschriebe, machte aus vier bewussten Entscheidungen
 * eine Vorgabe.
 *
 * Sie schließt auch nichts. Escape gehört weiter dem Dialog: Bei der
 * Kopierwahl gilt er nur, solange nicht kopiert wird, und diese Bedingung
 * steht dort richtig.
 *
 * Die Auswahl der Kandidaten und der Sprung sind **reine Funktionen** — der
 * Rest ist die Action, und die ist absichtlich dünn.
 */

/**
 * Was der Browser von sich aus anspringt.
 *
 * `[tabindex="-1"]` ist ausdrücklich **nicht** dabei: Solche Elemente sind
 * programmatisch fokussierbar, aber nicht Teil der Tab-Reihenfolge — sie hier
 * mitzuzählen hieße, den Fokus an Stellen zu schicken, an die er über die
 * Tastatur nie käme.
 *
 * `summary` steht in der Liste, weil ein Aufklapper ohne `tabindex` trotzdem
 * angesprungen wird; er kommt in diesen Dialogen (noch) nicht vor, und eine
 * Liste, die den Bestand beschreibt statt der Regel, wäre beim nächsten
 * Aufklapper still falsch.
 */
export const FOKUSSIERBAR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])'
].join(', ');

/**
 * Wohin der Fokus beim nächsten Tab springen muss — oder `null`.
 *
 * `null` heißt **nicht** „nichts tun kann man da", sondern „der Browser macht
 * es von selbst richtig". Genau das ist der Normalfall: Zwischen zwei Feldern
 * mitten im Dialog hat diese Funktion nichts zu entscheiden, und ein
 * erzwungener Sprung wäre dort schlimmer als keiner — er bräche die
 * Reihenfolge, die im Markup steht.
 *
 * Entschieden werden drei Fälle:
 *
 * 1. **Am Ende vorwärts** → an den Anfang. Das ist die Falle selbst.
 * 2. **Am Anfang rückwärts** → ans Ende. Dieselbe Falle, andere Richtung —
 *    ohne sie wandert Shift-Tab aus dem ersten Element in die Adresszeile.
 * 3. **Fokus liegt gar nicht im Dialog** → an den Anfang bzw. ans Ende. Der
 *    Fall tritt ein, wenn der Anfangsfokus nicht gesetzt werden konnte oder
 *    ein Element unter dem Fokus verschwunden ist; ohne diese Zeile führte der
 *    erste Tab dann in die Seite darunter.
 *
 * Eine leere Liste ergibt `null`: Ein Dialog ohne ein einziges anspringbares
 * Element hat keinen Ring, in dem man kreisen könnte, und den Tab dort
 * abzufangen hieße, die Tastatur festzuhalten.
 */
export function naechsterFokus<T>(
  kandidaten: readonly T[],
  aktiv: T | null | undefined,
  rueckwaerts = false
): T | null {
  if (!kandidaten.length) return null;
  const erster = kandidaten[0];
  const letzter = kandidaten[kandidaten.length - 1];
  const stelle = aktiv == null ? -1 : kandidaten.indexOf(aktiv);
  if (stelle < 0) return rueckwaerts ? letzter : erster;
  if (rueckwaerts) return stelle === 0 ? letzter : null;
  return stelle === kandidaten.length - 1 ? erster : null;
}

/**
 * Liegt das Element wirklich im Bild?
 *
 * `getClientRects()` und nicht `offsetParent`: Der Prüfweg über `offsetParent`
 * meldet bei jedem `position: fixed`-Element `null`, und genau so sind diese
 * Dialoge gebaut. Ein versteckter Knopf im Ring wäre die unangenehmste Sorte
 * Fehler — Tab landet auf etwas, das niemand sieht.
 *
 * Das gerade fokussierte Element zählt in jedem Fall mit: Sonst fiele es
 * mitten im Sprung aus der Liste, und `naechsterFokus` hielte den Fokus für
 * ausgewandert.
 */
function sichtbar(el: HTMLElement): boolean {
  return el.getClientRects().length > 0 || el === document.activeElement;
}

/**
 * Die Svelte-Action. `use:fokusfalle` an den Rahmen mit `role="dialog"`.
 *
 * Der Rückgabepunkt wird **beim Anbringen** gemerkt und nicht beim Schließen:
 * Zu dem Zeitpunkt steht der Fokus noch auf dem auslösenden Element — der
 * Lupe, dem Kopieren-Knopf, dem Löschen-Knopf. Danach setzt der Dialog seinen
 * Anfangsfokus, und wer erst dann fragte, bekäme den Abbrechen-Knopf des
 * Dialogs zurück, den es gleich nicht mehr gibt.
 *
 * Beim Abräumen wird nur zurückgesprungen, wenn das Element noch in der Seite
 * steht: Eine gelöschte Reise nimmt ihren Löschen-Knopf mit, und `focus()` auf
 * einem abgehängten Knoten verlegte den Fokus wortlos auf `<body>`.
 */
export function fokusfalle(node: HTMLElement) {
  const vorher = document.activeElement;

  function beiTaste(e: KeyboardEvent) {
    // Nur Tab, und keine Tabulator-Kombination mit Alt oder Strg: Die gehören
    // dem Browser (Tab-Wechsel), und sie abzufangen wäre ein Übergriff.
    if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey) return;
    const kandidaten = Array.from(
      node.querySelectorAll<HTMLElement>(FOKUSSIERBAR)
    ).filter(sichtbar);
    const ziel = naechsterFokus(
      kandidaten,
      document.activeElement as HTMLElement | null,
      e.shiftKey
    );
    if (!ziel) return;
    e.preventDefault();
    ziel.focus();
  }

  node.addEventListener('keydown', beiTaste);
  return {
    destroy() {
      node.removeEventListener('keydown', beiTaste);
      if (vorher instanceof HTMLElement && document.contains(vorher)) vorher.focus();
    }
  };
}
